import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { LuCircleCheck, LuCircleDashed, LuClock3, LuListTodo, LuTrash2, LuUserCheck, LuWrench } from 'react-icons/lu';
import DataCardList from '../../components/data/DataCardList';
import DataTable from '../../components/data/DataTable';
import DataToolbar from '../../components/data/DataToolbar';
import FilterSheet from '../../components/data/FilterSheet';
import StatTiles from '../../components/data/StatTiles';
import TablePagination from '../../components/data/TablePagination';
import ToggleChip from '../../components/data/ToggleChip';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import useElementWidth from '../../hooks/useElementWidth';
import useMediaQuery from '../../hooks/useMediaQuery';
import usePagedList from '../../hooks/usePagedList';
import useTableColumns from '../../hooks/useTableColumns';
import useViewportFit from '../../hooks/useViewportFit';
import AddTaskModal from './AddTaskModal';
import useTodoList, { isOverdue } from './useTodoList';
import useTodoText from './todoText';
import {
  ALL_STATUSES,
  COLUMN_KEYS,
  DEFAULT_VISIBLE_COLUMNS,
  FILTER_KEYS,
  MIN_COLUMN_WIDTH,
  PINNED_COLUMNS,
  PRIORITIES,
  STATUSES,
  TASK_COLUMNS,
  getFilterState,
} from './todoColumns';
import TaskCell, { TaskCardRow, useTaskCellTitle } from './parts/TaskCell';
import TaskDetailPane from './parts/TaskDetailPane';
import TodoHeader, { SortControl } from './parts/TodoHeader';

const COLUMNS_STORAGE_KEY = 'todoList.tableColumns.v1';
const DOCK_PANEL_QUERY = '(min-width: 1440px)';
const FIT_BOTTOM_GAP = 24;
const FIT_MIN_HEIGHT = 400;
const COMPACT_LIST_WIDTH = 640;
const COMPACT_PAGE_SIZE = 10;

const CARD_SURFACE = 'tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-rounded-[1.75rem] tw-border tw-border-white/80 tw-bg-white/80 tw-shadow-[inset_0_1px_0_rgba(255,255,255,1),0_20px_50px_-30px_rgba(15,23,42,0.35)] dark:tw-border-white/10 dark:tw-bg-[#22262f]/80 dark:tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_20px_50px_-30px_rgba(0,0,0,0.9)]';

/**
 * Yapılacaklar Listesi (Tailwind), built like the other data pages: status
 * tiles that filter, a table card that reaches at most the bottom of the
 * screen (status switch, quick filters, sorting, header filters, column
 * menu), the status changed right in the row, and the selected task's panel
 * (details, status, edit / delete) beside the table. Logic: useTodoList.
 */
const TodoListPage = () => {
  const todo = useTodoList();
  const {
    isAdmin, tasks, employees, isLoading, visibleTasks, stats,
    searchTerm, setSearchTerm, statusFilter, setStatusFilter, filters, handleFilter,
    onlyMine, setOnlyMine, onlyOverdue, setOnlyOverdue, clearFilters, sortConfig, setSortConfig,
    selectedTask, setSelectedTask, canChangeStatus, changeStatus, savingStatusId, deleteTask, fetchTasks,
  } = todo;
  const { language, locale, label, statusLabel, priorityLabel } = useTodoText();
  const getCellTitle = useTaskCellTitle();

  const columns = useTableColumns({
    storageKey: COLUMNS_STORAGE_KEY,
    columns: TASK_COLUMNS,
    defaultVisible: DEFAULT_VISIBLE_COLUMNS,
    minWidth: MIN_COLUMN_WIDTH,
  });
  const dockPanel = useMediaQuery(DOCK_PANEL_QUERY);
  const listCardRef = useRef(null);
  const compactList = useElementWidth(listCardRef, window.innerWidth) < COMPACT_LIST_WIDTH;
  const tableAreaRef = useRef(null);
  const fitHeight = useViewportFit(tableAreaRef, { bottomGap: FIT_BOTTOM_GAP, minHeight: FIT_MIN_HEIGHT });
  // Sayfa başına sabit 10 satır (kullanıcı isteği): kart ekrana sığdırılmaz, tablo satır sayısını ölçmez
  const maxCardHeight = undefined;
  const [showFilterSheet, setShowFilterSheet] = useState(false);
  const [form, setForm] = useState({ open: false, task: null, key: 0 });
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const hasLoaded = tasks.length > 0 || !isLoading;

  // Süzgeç, arama ya da sıra değişince ilk sayfaya dönülür
  const resetKey = JSON.stringify([searchTerm, statusFilter, filters, onlyMine, onlyOverdue, sortConfig]);
  const { pageRows, pageStart, currentPage, totalPages, pageSize, setPageSize, goToPage, reveal } = usePagedList(visibleTasks, { resetKey });

  useEffect(() => {
    if (compactList) setPageSize(COMPACT_PAGE_SIZE);
  }, [compactList, setPageSize]);

  // Escape closes the panel, unless a dialog or field has it
  useEffect(() => {
    if (!selectedTask) return undefined;
    const handleKeyDown = (event) => {
      if (event.key !== 'Escape' || event.defaultPrevented || showFilterSheet) return;
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target?.tagName)) return;
      setSelectedTask(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedTask, showFilterSheet, setSelectedTask]);

  const columnLabel = useCallback((key) => label(`columns.${key}`), [label]);

  const filterOptions = useMemo(() => ({
    priority: PRIORITIES.map((value) => ({ value, label: priorityLabel(value) })),
    assignee: employees.map((emp) => ({ value: emp.id, label: `${emp.eName} ${emp.eSurname}`.trim() })),
  }), [employees, priorityLabel]);

  const tableColumns = useMemo(() => columns.visibleKeys.map((key) => ({
    key,
    label: columnLabel(key),
    filter: TASK_COLUMNS[key].type ? { type: TASK_COLUMNS[key].type, options: filterOptions[key] } : undefined,
  })), [columns.visibleKeys, columnLabel, filterOptions]);

  const chipValue = (key) => {
    if (key === 'priority') return priorityLabel(filters.priority);
    if (key === 'assignee') return filterOptions.assignee.find((option) => option.value === filters.assignee)?.label || filters.assignee;
    return String(filters[key]);
  };
  const filterChips = FILTER_KEYS
    .filter((key) => getFilterState(key, filters[key]) !== 'off')
    .map((key) => ({ key, label: columnLabel(key), value: chipValue(key), state: 'applied' }));

  const isFilterActive = Object.values(filters).some(Boolean) || Boolean(searchTerm)
    || statusFilter !== ALL_STATUSES || onlyMine || onlyOverdue;

  // "Sayfaya git": 1 ile son sayfa arasında olmalı
  const handlePageJump = (text) => {
    const target = parseInt(text, 10);
    if (!Number.isFinite(target) || target < 1 || target > totalPages) {
      toast.error(label('toast.pageJumpInvalid', { max: totalPages }));
      return false;
    }
    goToPage(target);
    return true;
  };

  const selectedIndex = selectedTask ? visibleTasks.findIndex((task) => task.id === selectedTask.id) : -1;
  const selectSibling = (offset) => {
    const sibling = visibleTasks[selectedIndex + offset];
    if (!sibling) return;
    setSelectedTask(sibling);
    reveal(sibling.id);
  };

  const openForm = (task = null) => setForm((current) => ({ open: true, task, key: current.key + 1 }));
  const closeForm = () => setForm((current) => ({ ...current, open: false }));

  const confirmDelete = async () => {
    if (!taskToDelete) return;
    setDeleting(true);
    const ok = await deleteTask(taskToDelete.id);
    setDeleting(false);
    if (ok) {
      if (selectedTask?.id === taskToDelete.id) setSelectedTask(null);
      setTaskToDelete(null);
    }
  };

  const total = stats.total;
  const shareOf = (count) => (total > 0 ? Math.min(1, count / total) : 0);
  const shareTitle = (count) => label('stats.share', { percent: Math.round(shareOf(count) * 100) });
  const statusTile = (status, key, icon, gradient, barClass) => ({
    key,
    label: label(`stats.${key}`),
    shortLabel: label(`stats.${key}Short`),
    value: stats[key],
    icon,
    gradient,
    share: shareOf(stats[key]),
    shareTitle: shareTitle(stats[key]),
    barClass,
    onClick: () => setStatusFilter(statusFilter === status ? ALL_STATUSES : status),
    active: statusFilter === status,
  });
  const tiles = [
    { key: 'total', label: label('stats.total'), shortLabel: label('stats.totalShort'), value: total, icon: LuListTodo, gradient: 'tw-from-teal-400 tw-to-cyan-600 tw-shadow-teal-500/30', onClick: () => setStatusFilter(ALL_STATUSES), active: statusFilter === ALL_STATUSES },
    statusTile('pending', 'pending', LuCircleDashed, 'tw-from-slate-400 tw-to-slate-600 tw-shadow-slate-500/30', 'tw-bg-gradient-to-r tw-from-slate-300 tw-to-slate-500'),
    statusTile('in-progress', 'inProgress', LuWrench, 'tw-from-sky-400 tw-to-blue-600 tw-shadow-blue-500/30', 'tw-bg-gradient-to-r tw-from-sky-300 tw-to-blue-500'),
    statusTile('completed', 'completed', LuCircleCheck, 'tw-from-emerald-400 tw-to-teal-600 tw-shadow-emerald-500/30', 'tw-bg-gradient-to-r tw-from-emerald-400 tw-to-teal-500'),
  ];

  const quickFilters = (
    <>
      <ToggleChip pressed={onlyMine} onChange={setOnlyMine} icon={LuUserCheck} label={label('toolbar.mine')} title={label('toolbar.mineHint')} count={hasLoaded ? stats.mine : undefined} tone="teal" />
      <ToggleChip pressed={onlyOverdue} onChange={setOnlyOverdue} icon={LuClock3} label={label('toolbar.overdue')} title={label('toolbar.overdueHint')} count={hasLoaded ? stats.overdue : undefined} tone="amber" />
      <SortControl sortConfig={sortConfig} onChange={setSortConfig} />
    </>
  );

  const paneProps = {
    task: selectedTask,
    isAdmin,
    canChange: Boolean(selectedTask) && canChangeStatus(selectedTask),
    savingStatus: Boolean(selectedTask) && savingStatusId === selectedTask.id,
    position: selectedIndex >= 0 ? { index: selectedIndex, total: visibleTasks.length } : null,
    onPrev: selectedIndex > 0 ? () => selectSibling(-1) : undefined,
    onNext: selectedIndex >= 0 && selectedIndex < visibleTasks.length - 1 ? () => selectSibling(1) : undefined,
    onClose: () => setSelectedTask(null),
    onStatusChange: changeStatus,
    onEdit: () => openForm(selectedTask),
    onDelete: () => setTaskToDelete(selectedTask),
  };

  return (
    <>
      <div lang={language} className="tw-mx-auto tw-w-full tw-max-w-[1680px] tw-space-y-4 tw-font-sans tw-antialiased sm:tw-space-y-5 md:tw-px-4 lg:tw-px-6 2xl:tw-px-10">
        <TodoHeader isAdmin={isAdmin} onAdd={() => openForm(null)} />

        <StatTiles tiles={tiles} ready={hasLoaded} className="tw-grid-cols-2 lg:tw-grid-cols-4" />

        <div ref={tableAreaRef} className="tw-flex tw-items-start tw-gap-4">
          <section ref={listCardRef} aria-label={label('table.listLabel')} style={{ maxHeight: maxCardHeight }} className={CARD_SURFACE}>
            <DataToolbar
              search={{
                value: searchTerm,
                onChange: setSearchTerm,
                placeholder: label('toolbar.search'),
                placeholderShort: label('toolbar.searchShort'),
                label: label('toolbar.search'),
                minLength: 1,
              }}
              status={{
                options: [
                  { value: ALL_STATUSES, label: label('toolbar.all') },
                  ...STATUSES.map((value) => ({ value, label: statusLabel(value) })),
                ],
                value: statusFilter,
                onChange: setStatusFilter,
                layoutId: 'todo-status-filter',
                label: label('toolbar.status'),
              }}
              columnMenu={{
                columns: COLUMN_KEYS.map((key) => ({ key, label: columnLabel(key) })),
                visibleColumns: columns.visibleColumns,
                onToggle: columns.toggleColumn,
                onShowAll: columns.showAllColumns,
                onReset: columns.resetColumns,
              }}
              compact={compactList}
              onOpenFilters={() => setShowFilterSheet(true)}
              chips={filterChips}
              onRemoveChip={(key) => handleFilter(key, '')}
              onClearAll={clearFilters}
              extra={quickFilters}
            />

            {compactList ? (
              <DataCardList
                label={label('table.listLabel')}
                rows={pageRows}
                renderCard={(task) => <TaskCardRow task={task} />}
                selectedId={selectedTask?.id}
                onSelect={setSelectedTask}
                isLoading={isLoading && !hasLoaded}
                isBusy={isLoading && hasLoaded}
                emptyLabel={label('table.empty')}
                canClearFilters={isFilterActive}
                onClearFilters={clearFilters}
              />
            ) : (
              <DataTable
                label={label('table.listLabel')}
                rows={pageRows}
                columns={tableColumns}
                getColumnWidth={columns.getColumnWidth}
                onResizeColumn={columns.setColumnWidth}
                onResetColumnWidth={columns.resetColumnWidth}
                pinnedKeys={PINNED_COLUMNS}
                filters={filters}
                onFilterChange={handleFilter}
                getFilterState={getFilterState}
                renderCell={(task, key) => (
                  <TaskCell
                    columnKey={key}
                    task={task}
                    canChange={canChangeStatus(task)}
                    savingStatus={savingStatusId === task.id}
                    onStatusChange={changeStatus}
                  />
                )}
                getCellTitle={getCellTitle}
                getRowTone={(task) => (isOverdue(task) ? 'danger' : undefined)}
                selectedId={selectedTask?.id}
                onSelect={setSelectedTask}
                isLoading={isLoading && !hasLoaded}
                isBusy={isLoading && hasLoaded}
                skeletonRows={pageSize}
                maxCardHeight={maxCardHeight}
                onCapacityChange={setPageSize}
                emptyLabel={label('table.empty')}
                canClearFilters={isFilterActive}
                onClearFilters={clearFilters}
                onShowColumns={columns.resetColumns}
              />
            )}

            <TablePagination
              rangeStart={pageStart + 1}
              rangeEnd={pageStart + pageRows.length}
              resultCount={visibleTasks.length}
              rangeOf={label('table.rangeOf', { total: visibleTasks.length.toLocaleString(locale) })}
              ofTotal={isFilterActive ? label('table.ofTotal', { total: total.toLocaleString(locale) }) : undefined}
              currentPage={currentPage}
              totalPages={totalPages}
              isLoading={isLoading && hasLoaded}
              compact={compactList}
              onPage={goToPage}
              jump={{ label: label('table.jump'), onJump: handlePageJump }}
            />
          </section>

          {dockPanel && <TaskDetailPane docked height={fitHeight} {...paneProps} />}
        </div>
      </div>

      {!dockPanel && <TaskDetailPane {...paneProps} />}

      <FilterSheet
        isOpen={showFilterSheet}
        onClose={() => setShowFilterSheet(false)}
        fields={FILTER_KEYS.map((key) => ({ key, label: columnLabel(key), type: TASK_COLUMNS[key].type, options: filterOptions[key] }))}
        filters={filters}
        getFilterState={getFilterState}
        onFilterChange={handleFilter}
        onClearAll={clearFilters}
        resultCount={visibleTasks.length}
      />

      <AddTaskModal
        key={form.key}
        isOpen={form.open}
        onClose={closeForm}
        onUpdate={fetchTasks}
        employees={employees}
        editingTask={form.task}
      />

      <ConfirmDialog
        isOpen={Boolean(taskToDelete)}
        title={label('delete.title')}
        message={label('delete.message', { title: taskToDelete?.title || '' })}
        confirmLabel={label('delete.confirm')}
        cancelLabel={label('delete.cancel')}
        tone="danger"
        icon={LuTrash2}
        busy={deleting}
        onConfirm={confirmDelete}
        onCancel={() => !deleting && setTaskToDelete(null)}
      />
    </>
  );
};

export default TodoListPage;
